// Simulated camera feed for the hero. Three scenes, one per headline capability, drawn as
// inline SVG so they cost no image weight and stay sharp at any size. Scenes rotate on a
// timer; picking a tab selects one and stops the rotation.
import { Box, Flex, Text } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const SIGNAL = "#FF8A1F";
const CYAN = "#5BD5E8";
const INK = "#0A1020";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const ROTATE_MS = 5200;

/* ---------------------------------------------------------------- primitives */

// Detection overlay. The label sits on a filled tab above the box, as VMS overlays do.
const DetectionBox = ({ x, y, w, h, label, tone = SIGNAL }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} fill="none" stroke={tone} strokeWidth="1.4" />
    <rect x={x - 0.7} y={y - 10.5} width={label.length * 4.35 + 8} height="10" fill={tone} rx="1" />
    <text
      x={x + 3.3}
      y={y - 3}
      fontSize="6.6"
      fontWeight="700"
      fill={INK}
      fontFamily={MONO}
      letterSpacing="0.3"
    >
      {label}
    </text>
  </g>
);

// Walking figure as a stroked pictogram — the visual language a VMS already uses.
const Person = ({ x, y, s = 1, stride = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`} stroke="#C3D4E8" fill="none">
    <circle cx="0" cy="-56" r="6.2" fill="#C3D4E8" stroke="none" />
    <line x1="0" y1="-49" x2="0" y2="-26" strokeWidth="9" strokeLinecap="round" />
    <line x1="-1" y1="-44" x2={-10 * stride} y2="-33" strokeWidth="4.6" strokeLinecap="round" />
    <line x1="1" y1="-44" x2={9 * stride} y2="-36" strokeWidth="4.6" strokeLinecap="round" />
    <line x1="0" y1="-27" x2={-7 * stride} y2="-2" strokeWidth="5.6" strokeLinecap="round" />
    <line x1="0" y1="-27" x2={7 * stride} y2="-1" strokeWidth="5.6" strokeLinecap="round" />
  </g>
);

const Motorcycle = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <g stroke="#9FB6D2" fill="none" strokeWidth="2.4">
      <circle cx="-16" cy="-8" r="8" />
      <circle cx="16" cy="-8" r="8" />
      <path d="M-16 -8 L -4 -20 L 8 -20 L 16 -8" strokeWidth="2.8" strokeLinejoin="round" />
      <path d="M-4 -20 L 2 -9" />
      <path d="M8 -20 L 13 -27" />
    </g>
    <g stroke="#C3D4E8" fill="none">
      <circle cx="-1" cy="-40" r="4.6" fill="#C3D4E8" stroke="none" />
      <line x1="-1" y1="-35" x2="2" y2="-23" strokeWidth="6.4" strokeLinecap="round" />
      <line x1="1" y1="-32" x2="11" y2="-27" strokeWidth="3.6" strokeLinecap="round" />
    </g>
  </g>
);

const CarSide = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <path
      d="M-38 -10 L -34 -22 C -32 -27 -28 -30 -22 -30 L 10 -30 C 16 -30 21 -27 25 -23 L 33 -15 L 38 -13 C 40 -12 41 -11 41 -9 L 41 -4 L -38 -4 Z"
      fill="#243851"
      stroke="#9FB6D2"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M-30 -13 L -27 -24 L -3 -24 L -3 -13 Z" fill="#0E1826" />
    <path d="M2 -24 L 8 -24 C 13 -24 17 -22 20 -18 L 24 -13 L 2 -13 Z" fill="#0E1826" />
    <circle cx="-22" cy="-4" r="7.5" fill="#0B121D" stroke="#9FB6D2" strokeWidth="2" />
    <circle cx="24" cy="-4" r="7.5" fill="#0B121D" stroke="#9FB6D2" strokeWidth="2" />
  </g>
);

const CarRear = ({ x, y, s = 1, plate }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <path
      d="M-46 0 L-46 -34 C-46 -44 -40 -52 -30 -55 L-24 -68 C-22 -73 -18 -76 -12 -76 L12 -76 C18 -76 22 -73 24 -68 L30 -55 C40 -52 46 -44 46 -34 L46 0 Z"
      fill="#243851"
      stroke="#9FB6D2"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M-22 -56 L-17 -70 L17 -70 L22 -56 Z" fill="#0E1826" />
    <rect x="-42" y="-40" width="15" height="9" rx="2.2" fill="#C0392F" />
    <rect x="27" y="-40" width="15" height="9" rx="2.2" fill="#C0392F" />
    <rect x="-46" y="-14" width="92" height="8" fill="#1A2A3E" />
    <rect x="-21" y="-29" width="42" height="15" rx="1.6" fill="#F3F6FA" stroke="#8FA3BE" strokeWidth="1" />
    <text x="0" y="-18" fontSize="8.4" fontWeight="700" fill="#16233A" textAnchor="middle" fontFamily={MONO}>
      {plate}
    </text>
  </g>
);

/* ------------------------------------------------------------------- scenes */

const GroundPlane = () => (
  <g>
    <rect width="320" height="200" fill="url(#lvSky)" />
    <rect y="86" width="320" height="114" fill="url(#lvGround)" />
    <line x1="0" y1="86" x2="320" y2="86" stroke="#31506F" strokeWidth="1" opacity="0.7" />
    <ellipse cx="86" cy="150" rx="96" ry="44" fill="url(#lvPool)" />
    <ellipse cx="238" cy="136" rx="74" ry="34" fill="url(#lvPool)" />
  </g>
);

const SceneLineCross = ({ t, backdrop }) => (
  <g>
    {!backdrop && <GroundPlane />}
    <line x1="176" y1="56" x2="140" y2="198" stroke={SIGNAL} strokeWidth="1.6" strokeDasharray="6 4" />
    <circle cx="176" cy="56" r="2.6" fill={SIGNAL} />
    <circle cx="140" cy="198" r="2.6" fill={SIGNAL} />
    <g stroke={SIGNAL} strokeWidth="1.6" fill="none" opacity="0.85">
      <line x1="112" y1="188" x2="176" y2="188" />
      <path d="M170 183 L177 188 L170 193" strokeLinejoin="round" strokeLinecap="round" />
    </g>
    <Person x={154} y={174} s={1} stride={1} />
    <DetectionBox x={132} y={106} w={44} h={72} label="PERSON 98%" />
    <g>
      <rect x="198" y="62" width={t("hero.alertLineCross").length * 4.6 + 12} height="13" rx="1.6" fill={SIGNAL} />
      <text x="203" y="71.4" fontSize="7.2" fontWeight="700" fill={INK} fontFamily={MONO}>
        {t("hero.alertLineCross")}
      </text>
    </g>
  </g>
);

const SceneObjects = ({ backdrop }) => (
  <g>
    {!backdrop && <GroundPlane />}
    <Person x={52} y={178} s={0.92} stride={-1} />
    <Motorcycle x={140} y={176} s={1.05} />
    <CarSide x={244} y={176} s={1.1} />
    <DetectionBox x={32} y={114} w={40} h={66} label="PERSON 97%" />
    <DetectionBox x={108} y={130} w={64} h={48} label="MOTORCYCLE 92%" tone={CYAN} />
    <DetectionBox x={196} y={136} w={96} h={44} label="VEHICLE 94%" />
  </g>
);

const SceneLicensePlate = ({ t, plate, backdrop }) => (
  <g>
    {!backdrop && <GroundPlane />}
    <CarRear x={112} y={182} s={1.02} plate={plate} />
    <DetectionBox x={90} y={102} w={96} h={80} label="VEHICLE 96%" />
    <rect x="90" y="152" width="44" height="16" fill="none" stroke={SIGNAL} strokeWidth="1.4" />
    <g stroke={SIGNAL} strokeWidth="1" opacity="0.55" fill="none">
      <line x1="134" y1="152" x2="214" y2="104" />
      <line x1="134" y1="168" x2="214" y2="156" />
    </g>
    <g>
      <rect x="214" y="100" width="94" height="58" rx="3" fill="#0B1524" stroke={SIGNAL} strokeWidth="1.2" />
      <text x="222" y="114" fontSize="6.6" fontWeight="700" fill={SIGNAL} fontFamily={MONO} letterSpacing="0.5">
        {t("hero.calloutPlate")}
      </text>
      <rect x="222" y="121" width="78" height="28" rx="2" fill="#F3F6FA" />
      <text x="261" y="141" fontSize="15" fontWeight="700" fill="#16233A" textAnchor="middle" fontFamily={MONO}>
        {plate}
      </text>
    </g>
  </g>
);

/* ----------------------------------------------------------------- the panel */

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;

const sweep = keyframes`
  0% { top: -64px; }
  100% { top: 100%; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const SCENES = [
  { id: "lineCross", Component: SceneLineCross },
  { id: "objectDetection", Component: SceneObjects },
  { id: "licensePlate", Component: SceneLicensePlate },
];

// Optional real camera stills. Drop <id>.jpg into public/scenes/ and it is picked up
// automatically; until then each scene falls back to its drawn ground plane. Files live in
// public/ rather than src/ so a missing one 404s quietly instead of breaking the build.
const sceneImageUrl = (id) => `${process.env.PUBLIC_URL || ""}/scenes/${id}.jpg`;

// Probes each still once and reports which ones actually loaded.
const useSceneImages = () => {
  const [loaded, setLoaded] = useState({});

  useEffect(() => {
    let alive = true;
    const images = SCENES.map(({ id }) => {
      const img = new window.Image();
      img.onload = () => {
        if (alive) setLoaded((prev) => ({ ...prev, [id]: sceneImageUrl(id) }));
      };
      img.src = sceneImageUrl(id);
      return img;
    });
    return () => {
      alive = false;
      images.forEach((img) => {
        img.onload = null;
      });
    };
  }, []);

  return loaded;
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const SceneTab = ({ active, onClick, children }) => (
  <Text
    as="button"
    type="button"
    onClick={onClick}
    aria-pressed={active}
    fontFamily="mono"
    fontSize="9.5px"
    letterSpacing="0.09em"
    textTransform="uppercase"
    fontWeight={active ? "700" : "400"}
    border="1px solid"
    borderColor={active ? SIGNAL : "rgba(127, 182, 240, 0.24)"}
    bg={active ? SIGNAL : "transparent"}
    color={active ? INK : "#9FB6D2"}
    borderRadius="2px"
    px="8px"
    py="3px"
    transition="background 0.15s ease, color 0.15s ease, border-color 0.15s ease"
    _hover={active ? {} : { borderColor: "rgba(127, 182, 240, 0.55)", color: "#CFE0F2" }}
    _focusVisible={{ outline: "2px solid", outlineColor: SIGNAL, outlineOffset: "2px" }}
  >
    {children}
  </Text>
);

const SpecChip = ({ children }) => (
  <Text
    as="span"
    fontFamily="mono"
    fontSize="9.5px"
    letterSpacing="0.09em"
    textTransform="uppercase"
    color="#6F82A0"
    border="1px solid rgba(127, 182, 240, 0.14)"
    borderRadius="2px"
    px="8px"
    py="3px"
  >
    {children}
  </Text>
);

const LiveView = ({ channel }) => {
  const { t } = useTranslation("home");
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const sceneImages = useSceneImages();

  const plate = t("hero.samplePlate");
  const specChips = t("hero.specChips", { returnObjects: true });
  const specList = Array.isArray(specChips) ? specChips : [];

  useEffect(() => {
    if (paused || prefersReducedMotion()) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % SCENES.length), ROTATE_MS);
    return () => window.clearInterval(id);
  }, [paused]);

  // An explicit choice wins over the carousel.
  const select = useCallback((i) => {
    setIndex(i);
    setPaused(true);
  }, []);

  const timestamp = useMemo(() => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(
      d.getMinutes()
    )}:${p(d.getSeconds())}`;
  }, []);

  const Active = SCENES[index].Component;
  const backdrop = sceneImages[SCENES[index].id];

  return (
    <Box
      border="1px solid rgba(127, 192, 240, 0.22)"
      borderRadius="5px"
      overflow="hidden"
      bg="#070B14"
      boxShadow="0 24px 50px rgba(0, 0, 0, 0.42)"
    >
      <Flex
        align="center"
        gap="10px"
        px="12px"
        py="8px"
        bg="#0E1727"
        borderBottom="1px solid rgba(127, 192, 240, 0.16)"
        fontFamily="mono"
        fontSize="10px"
        letterSpacing="0.09em"
        textTransform="uppercase"
        color="#8FA3BE"
      >
        <Flex align="center" gap="5px" color="#FF6B5A" flex="none">
          <Box
            w="6px"
            h="6px"
            borderRadius="50%"
            bg="#FF6B5A"
            animation={`${blink} 2s ease-in-out infinite`}
            sx={{ "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
          />
          REC
        </Flex>
        <Text as="span" noOfLines={1}>
          {channel}
        </Text>
        <Text
          as="span"
          ml="auto"
          flex="none"
          display={{ base: "none", md: "block" }}
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {timestamp}
        </Text>
      </Flex>

      <Box position="relative" overflow="hidden" sx={{ aspectRatio: "16 / 10" }} bg="#080D18">
        {backdrop && (
          <Box
            position="absolute"
            inset="0"
            bgImage={`url(${backdrop})`}
            bgSize="cover"
            bgPosition="center"
          />
        )}
        <svg
          viewBox="0 0 320 200"
          width="100%"
          height="100%"
          style={{ display: "block", position: "relative" }}
          role="img"
          aria-label={t(`hero.scenes.${SCENES[index].id}`)}
        >
          <defs>
            <linearGradient id="lvSky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#101B2E" />
              <stop offset="100%" stopColor="#0B1220" />
            </linearGradient>
            <linearGradient id="lvGround" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1B2E48" />
              <stop offset="100%" stopColor="#080D18" />
            </linearGradient>
            <radialGradient id="lvPool">
              <stop offset="0%" stopColor="#96BEE1" stopOpacity="0.20" />
              <stop offset="100%" stopColor="#96BEE1" stopOpacity="0" />
            </radialGradient>
          </defs>
          <Box as="g" key={SCENES[index].id} animation={`${fadeIn} 0.45s ease`}>
            <Active t={t} plate={plate} backdrop={backdrop} />
          </Box>
        </svg>

        <Box
          position="absolute"
          inset="0"
          pointerEvents="none"
          bgImage="repeating-linear-gradient(180deg, rgba(255, 255, 255, 0.028) 0 1px, rgba(0, 0, 0, 0) 1px 3px)"
        />
        <Box
          position="absolute"
          left="0"
          right="0"
          h="64px"
          pointerEvents="none"
          bgImage="linear-gradient(180deg, rgba(91, 213, 232, 0) 0%, rgba(91, 213, 232, 0.09) 60%, rgba(91, 213, 232, 0) 100%)"
          animation={`${sweep} 6.5s linear infinite`}
          sx={{
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              top: "38%",
              opacity: 0.35,
            },
          }}
        />
      </Box>

      <Flex
        gap="6px"
        flexWrap="wrap"
        align="center"
        px="12px"
        py="10px"
        bg="#0E1727"
        borderTop="1px solid rgba(127, 192, 240, 0.16)"
      >
        {SCENES.map((scene, i) => (
          <SceneTab key={scene.id} active={i === index} onClick={() => select(i)}>
            {t(`hero.scenes.${scene.id}`)}
          </SceneTab>
        ))}
        <Flex gap="6px" ml={{ base: "0", lg: "auto" }} flexWrap="wrap">
          {specList.map((chip) => (
            <SpecChip key={chip}>{chip}</SpecChip>
          ))}
        </Flex>
      </Flex>
    </Box>
  );
};

export default LiveView;
